import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Kbd } from "@/components/ui/input";
import { Switch } from "@/components/ui/misc";
import type { DesktopPrefs, DesktopPrefsPatch } from "@/types/bridge";
import { acceleratorFromEvent, acceleratorKeys } from "@/features/keyboard/shortcuts";
import { Row, Section } from "./parts";

const DEFAULT_SHORTCUT = "Alt+CommandOrControl+N";

function Keycaps({ accelerator }: { accelerator: string }) {
  return (
    <span className="inline-flex gap-0.5">
      {acceleratorKeys(accelerator).map((k, i) => (
        <Kbd key={i} className="h-[22px] min-w-[22px] text-[12px]">
          {k}
        </Kbd>
      ))}
    </span>
  );
}

/** Global quick-add shortcut: shows the current keys, records a new combination, or turns it off. */
function ShortcutControl({ prefs, update }: { prefs: DesktopPrefs; update: (p: DesktopPrefsPatch) => Promise<unknown> }) {
  const [recording, setRecording] = useState(false);
  useEffect(() => {
    if (!recording) return;
    const onKey = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.key === "Escape") return setRecording(false);
      const accelerator = acceleratorFromEvent(e);
      if (!accelerator) return;
      setRecording(false);
      void update({ quickAddShortcut: accelerator }).catch((err: Error) => toast.error("Couldn't use that shortcut", { description: err.message }));
    };
    // Capture, so the app's own shortcuts don't run while recording.
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [recording, update]);

  const off = !prefs.quickAddShortcut;
  return (
    <>
      {recording ? (
        <span className="text-[13px] text-muted-foreground" aria-live="polite">
          Press a shortcut…
        </span>
      ) : off ? (
        <span className="text-[13px] text-muted-foreground">Off</span>
      ) : (
        <Keycaps accelerator={prefs.quickAddShortcut} />
      )}
      <Button size="sm" variant="outline" onClick={() => setRecording(!recording)}>
        {recording ? "Cancel" : "Change"}
      </Button>
      {!recording && (off || prefs.quickAddShortcut !== DEFAULT_SHORTCUT) && (
        <Button size="sm" variant="ghost" onClick={() => void update({ quickAddShortcut: DEFAULT_SHORTCUT }).catch((err: Error) => toast.error("Couldn't use that shortcut", { description: err.message }))}>
          Reset
        </Button>
      )}
      {!recording && !off && (
        <Button size="sm" variant="ghost" onClick={() => void update({ quickAddShortcut: "" })}>
          Turn off
        </Button>
      )}
    </>
  );
}

/** Mac-only preferences held by the Electron shell. */
export function DesktopSection({ prefs, update }: { prefs: DesktopPrefs; update: (p: DesktopPrefsPatch) => Promise<unknown> }) {
  return (
    <Section title="Desktop">
      <Row label="Open at login" hint={prefs.launchAtLoginSupported ? undefined : "Available in the packaged app."}>
        <Switch
          checked={prefs.launchAtLogin}
          disabled={!prefs.launchAtLoginSupported}
          onCheckedChange={(v) => void update({ launchAtLogin: v }).catch((err: Error) => toast.error("Couldn't change open at login", { description: err.message }))}
          aria-label="Open at login"
        />
      </Row>
      <Row
        label="New task from any app"
        error={prefs.quickAddShortcutStatus === "failed"}
        hint={prefs.quickAddShortcutStatus === "failed" ? "Desk couldn't register this shortcut, so it isn't active. Choose a different one." : undefined}
      >
        <ShortcutControl prefs={prefs} update={update} />
      </Row>
    </Section>
  );
}
